import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { updateUser, type UpdateUserRequest } from '../api/users';

export const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    email: user?.email || '',
    walletAddress: user?.walletAddress || '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const queryClient = useQueryClient();

  const updateUserMutation = useMutation({
    mutationFn: (data: UpdateUserRequest) => updateUser(user!.id, data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Refresh user data in AuthContext to reflect changes immediately
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (error) => {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to update profile' 
      });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const updateData: UpdateUserRequest = {};
    
    if (formData.email !== user.email) {
      updateData.email = formData.email;
    }

    if (Object.keys(updateData).length === 0) {
      setMessage({ type: 'error', text: 'No changes to save' });
      return;
    }

    updateUserMutation.mutate(updateData);
  };

  const handleReset = () => {
    setFormData({
      email: user?.email || '',
      walletAddress: user?.walletAddress || '',
    });
    setMessage(null);
  };

  return (
    <div className="profile">
      <div className="page-header">
        <h1>Profile Settings</h1>
        <p>Manage your account information and wallet address</p>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-info">
            <div className="profile-avatar-large">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div className="profile-details">
              <h2>{user?.firstName} {user?.lastName}</h2>
              <p className="profile-role">{user?.role}</p>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <h3>Account Information</h3>
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <div className="input-container">
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={updateUserMutation.isPending}
                  />
                </div>
                <p className="field-help">
                  This email will be used for login and notifications
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="walletAddress">Wallet Address</label>
                <div className="input-container">
                  <input
                    id="walletAddress"
                    type="text"
                    value={formData.walletAddress}
                    readOnly
                    placeholder="0x..."
                    className="readonly-field"
                  />
                </div>
                <p className="field-help">
                  Contact an administrator to update your wallet address
                </p>
              </div>
            </div>

            {message && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="reset-button"
                disabled={updateUserMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        <div className="profile-sidebar">
          <div className="info-card">
            <h3>Account Information</h3>
            <div className="info-item">
              <span className="info-label">User ID:</span>
              <span className="info-value">{user?.id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Role:</span>
              <span className="info-value">{user?.role}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Member Since:</span>
              <span className="info-value">
                {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>

          <div className="help-card">
            <h3>Need Help?</h3>
            <p>If you need to change your name, role or wallet address, please contact an administrator.</p>
            <p>Your wallet address is used to display data on Dashboard and My Positions.</p>
          </div>
        </div>
      </div>

    </div>
  );
};
